import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";

const WHY = [
    {
        title: "Hyper-local reach",
        copy: "Your name next to the stories the Twin Cities actually talks about — the artists, the venues, the neighborhood favorites.",
    },
    {
        title: "Authentic storytelling",
        copy: "No scripts, no fluff. Real conversations that show people who you are and why your work matters.",
    },
    {
        title: "Community trust",
        copy: "Built on showing up for local artists, businesses, and organizations. When CBJ vouches for a story, people listen.",
    },
];

const WAYS = [
    {
        n: "01",
        title: "Sponsorships & partnerships",
        copy: "Put your brand behind the platform that celebrates the Twin Cities — episodes, events, and series sponsorships.",
        type: "Partnership or sponsorship",
        cta: "Start a partnership",
    },
    {
        n: "02",
        title: "Artist & business features",
        copy: "Get checked in on. Spotlights and behind-the-scenes features for the people building the local scene.",
        type: "Interview request",
        cta: "Get featured",
    },
    {
        n: "03",
        title: "Media inquiries",
        copy: "Interviews with Courtnie, press questions, and speaking requests — all welcome.",
        type: "Media inquiry",
        cta: "Reach the media desk",
    },
    {
        n: "04",
        title: "Interview requests",
        copy: "Want to be a guest or recommend someone? Real talk only — authentic, unfiltered, community focused.",
        type: "Interview request",
        cta: "Request an interview",
    },
];

export default function WorkWithCBJ() {
    return (
        <>
            <PageHeader
                eyebrow="Sponsors · Partners · Media"
                title="Work With CBJ"
                description="For sponsors, partnerships, collaborations, media inquiries, and interview requests — this platform is built on community, and we'd love to build with you."
            />

            <section className="pb-24 sm:pb-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">Why partner with us</p>
                    </Reveal>
                    <div className="mt-8 grid gap-5 md:grid-cols-3" data-testid="why-partner-grid">
                        {WHY.map((w, i) => (
                            <Reveal key={w.title} delay={0.08 * i}>
                                <div className="group h-full border border-white/10 bg-coal/50 p-8 transition-colors hover:border-neon-pink/50">
                                    <span className="font-mono text-xs tracking-[0.24em] text-neon-pink">
                                        0{i + 1}
                                    </span>
                                    <h3 className="mt-4 font-display text-xl font-bold uppercase">
                                        {w.title}
                                    </h3>
                                    <p className="mt-4 text-sm leading-relaxed text-fog">
                                        {w.copy}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-white/10 bg-coal/40 py-24 sm:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">Ways to work together</p>
                        <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">
                            Pick your check-in
                        </h2>
                    </Reveal>
                    <div className="mt-10">
                        {WAYS.map((way, i) => (
                            <Reveal key={way.n} delay={0.05 * i}>
                                <div
                                    className="group flex flex-col gap-5 border-t border-white/10 py-8 last:border-b sm:flex-row sm:items-center sm:justify-between"
                                    data-testid={`workwith-row-${way.n}`}
                                >
                                    <div className="flex items-baseline gap-5 sm:gap-10">
                                        <span className="font-mono text-xs text-neon-pink">
                                            {way.n}
                                        </span>
                                        <div>
                                            <h3 className="font-display text-xl font-bold uppercase sm:text-2xl">
                                                {way.title}
                                            </h3>
                                            <p className="mt-2 max-w-xl text-sm leading-relaxed text-fog">
                                                {way.copy}
                                            </p>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/contact?type=${encodeURIComponent(
                                            way.type,
                                        )}`}
                                        data-testid={`workwith-cta-${way.n}`}
                                        className="group/link flex shrink-0 items-center gap-2 border border-white/20 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-silver transition-all hover:border-neon-pink hover:text-neon-pink"
                                    >
                                        {way.cta}
                                        <ArrowRight
                                            size={14}
                                            className="transition-transform group-hover/link:translate-x-1"
                                        />
                                    </Link>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-fog">
                            Every partnership backs the rallying cry — support
                            local · support live music · support each other
                        </p>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
